import { ROUND_SECONDS, type FinalScore } from '@/game/types';
import { streakMultiplierLabel } from '@/game/scoring';
import { tierNumeral } from '@/game/xp';
import { formatScore } from '@/ui/hud';
import { endReasonCopy } from '@/ui/results-layout';

/**
 * The share card's composition, as pure data: a list of draw ops the canvas
 * renderer (`card.ts`) executes. No DOM, no canvas — the layout is testable
 * in Node and every number in it is a named constant below. Two sizes come
 * from one function: the STORY (1080×1920, the share sheet) and the LINK
 * (1200×630, what a pasted link unfurls into), the same content re-flowed.
 *
 * Blockari's card, 1:1 in its geometry, in 21 Wild's colours and with 21
 * Wild's art: the felt's pool of light painted on the canvas and the
 * wordmark (`/logo.webp`, the same file the hero draws) above the plate; a
 * dark plate carries every word — text never lands on raw art.
 *
 * Three states. OPEN: the creator has played, nobody has taken it — the score
 * is HIDDEN until someone takes it (the hero slot says "BEAT MY SCORE"), the pot
 * is the second hero, the code slab and the take QR under it. COMPLETE: matched and settled — the pot's slot holds the OUTCOME
 * (won: "+payout $CHAIN" with the coin; lost: "−stake $CHAIN"), no code, no
 * take link, the QR and the line point at the front door. SOLO: the front
 * door alone.
 *
 * The card is an image with its own type scale; the shell's "nothing under
 * 12 px" rule is about the UI, not about this.
 */

export type CardSize = 'story' | 'link';

/** Export pixel sizes. The link size is what scrapers expect (`og:image`). */
export const CARD_PX: Record<CardSize, { w: number; h: number }> = {
  story: { w: 1080, h: 1920 },
  link: { w: 1200, h: 630 },
};

export interface CardRank {
  index: number;
  name: string;
  tier: number;
}

export interface CardUser {
  username: string;
  xpLevel: number;
  rank: CardRank;
}

/** A settled challenge, as the results plate reports it. */
export interface CardOutcome {
  won: boolean;
  /** What moved: the payout on a win, the stake on a loss. */
  chain: number;
  opponent: string;
  /** Their score; null when they never played it (a forfeit: no number to show). */
  theirScore: number | null;
}

export interface CardChallenge {
  /** The six-character code; printed and encoded only while the challenge is open. A taker's seat may not carry one. */
  code: string | null;
  /** The stake, when known (the pot is twice it). */
  entryFee?: number | undefined;
  /** Open: nobody has taken it. Complete: matched and settled. */
  status: 'open' | 'complete';
  /** The settlement, for a complete challenge. */
  outcome?: CardOutcome | undefined;
}

export interface CardInput {
  size: CardSize;
  final: FinalScore;
  /** The session user; null when nobody is signed in (a solo run) — the player line is left out. */
  user: CardUser | null;
  /**
   * An open challenge's creator stays anonymous until someone takes it: the identity pill
   * (name, rank line) is left off the card.
   */
  anonymous?: boolean | undefined;
  /** A challenge game; absent on a solo round. */
  challenge?: CardChallenge | undefined;
  /**
   * A SETTLED challenge shared to invite the next one ("Challenge Me", from the settled screen): the door
   * under the outcome reads "SCAN TO TAKE ON <NAME>" over "CHALLENGE ME", and the QR encodes this player's
   * profile at its challenge card (`challengeMeUrl`) — never a take link: open challenges are anonymous and a
   * card must not point at one.
   */
  challengeMe?: { username: string } | undefined;
  /** `location.host` — no scheme, so the link reads as a handle: `21wild.example.com/?code=…`. */
  host: string;
  /** `location.origin` — with the scheme, for the QR code (a scanner needs a full URL). */
  origin: string;
}

export type Face = 'display' | 'body';
/** Colour roles; the renderer resolves them to the design tokens. */
export type Tone = 'text' | 'dim' | 'mint' | 'amber' | 'rose' | 'indigo' | 'rank';

export interface TextOp {
  kind: 'text';
  id: string;
  text: string;
  x: number;
  /** Vertical centre of the line (the renderer draws with a middle baseline). */
  y: number;
  size: number;
  face: Face;
  weight: 400 | 500 | 600 | 700;
  tone: Tone;
  align: 'left' | 'center' | 'right';
  /** Letter-spacing in em. */
  track: number;
  /** Soft glow radius in px, in the text's own tone; 0 = none. */
  glow: number;
  alpha: number;
}
/**
 * The key art, full-bleed: the felt under a night sky, the wordmark on its
 * arc, the cards. The renderer paints the felt's pool of light over it and,
 * should the file not load, a painted ground and the wordmark in its box —
 * so the plate never sits on nothing.
 */
export interface ArtOp {
  kind: 'art';
  src: string;
  w: number;
  h: number;
  /** The part of the picture to draw, in its own pixels, stretched over the whole card: a uniform zoom. Absent = all of it. */
  crop?: { x: number; y: number; w: number; h: number };
  /** Where the wordmark sits when the art is not there: its centre and box. */
  wordmark: { src: string; cx: number; y: number; w: number; h: number };
}
/** The score plate: a dark rounded slab with a rim, an outer shadow and a fade melting the art into its top edge. */
export interface PlateOp {
  kind: 'plate';
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
  /** Height of the transparent → plate fade above the top edge. */
  fade: number;
}
/** A tinted panel behind a block (the pot, the outcome): a rounded fill with a rim in its tone. */
export interface PanelOp {
  kind: 'panel';
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
  tone: Tone;
  /** Fill and rim alphas over the tone. */
  fill: number;
  edge: number;
}

/** The glass surfaces on the plate: the tiles and the identity pill (white 0.05 / 0.08), the pot and the outcome (their tone). */
export const GLASS = { fill: 0.05, edge: 0.08 } as const;
export const POT_GLASS = { fill: 0.2, edge: 0.5 } as const;
export interface BadgeOp {
  kind: 'badge';
  x: number;
  y: number;
  size: number;
  rank: CardRank;
}
/** The code slab: the challenge code in the `.code` style — tracked, mint, dashed rim on ink. */
export interface SlabOp {
  kind: 'slab';
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
  text: string;
  size: number;
  track: number;
}
export interface CoinOp {
  kind: 'coin';
  x: number;
  y: number;
  size: number;
}
/** A QR code of `text`, dark modules on a light rounded tile so any scanner reads it off a screen. */
export interface QrOp {
  kind: 'qr';
  id: string;
  /** The tile's box; the modules sit inside `pad`. */
  x: number;
  y: number;
  size: number;
  pad: number;
  r: number;
  text: string;
}
export type CardOp =
  | TextOp
  | ArtOp
  | PlateOp
  | PanelOp
  | BadgeOp
  | SlabOp
  | CoinOp
  | QrOp;

export interface CardLayout {
  size: CardSize;
  w: number;
  h: number;
  ops: CardOp[];
}

/** The wordmark: the hero's file, so the card cannot drift from the screen. */
export const WORDMARK_SRC = '/logo.webp';

/** The key art behind each size (public/brand): the wordmark is painted into it, on its arc. */
export const CARD_ART: Record<CardSize, string> = {
  story: '/brand/share-bg-1080x1920.jpg',
  link: '/brand/og-card-1200x630.jpg',
};

/** Measures a text run in px; the renderer passes `measureText`, tests use the estimate. */
export type Measure = (
  text: string,
  face: Face,
  size: number,
  weight: number,
  track: number,
) => number;

/** Average advance per glyph in em for the estimate (the display face is condensed; the body is not). */
const GLYPH_EM: Record<Face, number> = { display: 0.5, body: 0.58 };

/** A width estimate for when nothing can measure (tests, a renderer without fonts). */
export const estimateWidth: Measure = (text, face, size, _weight, track) =>
  text.length * size * (GLYPH_EM[face] + track);

/* --------------------------------------------------------------------------
   Copy
   -------------------------------------------------------------------------- */


const MINUTES = ROUND_SECONDS / 60;

export { formatScore };

/**
 * The line that travels with the card (the share sheet's text, the preview's
 * copy box). Open (the score stays hidden until the challenge is taken): "Beat
 * my score on 21 Wild. Same deck, 3 minutes — code AGB2BS". Complete, won: "I scored 9,520 on 21 Wild and won 20 $CHAIN —
 * play free: host"; lost: the score alone with the front door. Solo: the
 * same as open without the dare.
 */
export function shareText(total: number, host: string, challenge?: CardChallenge | undefined): string {
  if (hidesScore(challenge)) {
    return `${HIDDEN_TEXT_LEAD}. Same deck, ${MINUTES} minutes \u2014 code ${openCode(challenge)}`;
  }
  const scored = `I scored ${formatScore(total)} on 21 Wild`;
  if (challenge?.status === 'complete') {
    const o = challenge.outcome;
    const won = o?.won ? ` and won ${formatScore(o.chain)} $CHAIN` : '';
    return `${scored}${won} — play free: ${host}`;
  }
  const lead = `${scored}. Same deck, ${MINUTES} minutes`;
  const code = openCode(challenge);
  return code ? `${lead} — beat me: ${code}` : `${lead}.`;
}

/** The eyebrow over the score: how the round ended, in the plate's words. */
export function endLabel(final: FinalScore): string {
  return endReasonCopy(final.reason);
}

/** "MASON II · XP LV 12": the account line under the name. */
export function rankLine(user: CardUser): string {
  return `${user.rank.name} ${tierNumeral(user.rank.tier)}`.toUpperCase() + ` · XP LV ${user.xpLevel}`;
}

/** The three stat tiles: clears, best streak, level — the results panel's headline rows (the level in mint). */
export function statTiles(final: FinalScore): { label: string; value: string; tone: Tone }[] {
  return [
    { label: 'CLEARS', value: String(final.clears), tone: 'text' },
    // `bestStreak` is a level (0 none, 1..5 = 2x..6x); the shared label turns it into the multiplier.
    { label: 'BEST STREAK', value: streakMultiplierLabel(final.bestStreak), tone: 'text' },
    { label: 'LEVEL', value: `LV ${final.levelReached}`, tone: 'mint' },
  ];
}

export const SCORE_LABEL = 'SCORE';
/** What an open challenge's card says where the score would be: no number, a dare. */
export const HIDDEN_SCORE_LABEL = 'CHALLENGE';
export const HIDDEN_SCORE = 'BEAT MY SCORE';
const HIDDEN_TEXT_LEAD = 'Beat my score on 21 Wild';

/** The pot block's three lines: "WIN THE POT" / "200" + "$CHAIN" / "Stake 100 to play". The score is never on it. */
export function potCopy(entryFee: number) {
  return {
    eyebrow: 'WIN THE POT',
    value: formatScore(entryFee * 2),
    unit: '$CHAIN',
    stake: `Stake ${formatScore(entryFee)} to play`,
  };
}

/** The outcome block's three lines: "YOU WON" / "+20" + "$CHAIN" / "vs mason · 12,310". */
export function outcomeCardCopy(o: CardOutcome) {
  return {
    eyebrow: o.won ? 'YOU WON' : 'YOU LOST',
    value: `${o.won ? '+' : '−'}${formatScore(o.chain)}`,
    unit: '$CHAIN',
    vs: `vs ${o.opponent} · ${o.theirScore === null ? 'forfeit' : formatScore(o.theirScore)}`,
    tone: (o.won ? 'mint' : 'rose') as Tone,
  };
}

export const SCAN_LABEL = 'SCAN TO PLAY';
export const CTA_LABEL = 'PLAY FREE AT';
/** The Challenge Me card's call to action, over "SCAN TO TAKE ON <NAME>". */
export const CHALLENGE_ME_HERO = 'CHALLENGE ME';
export const scanToTake = (username: string): string => `SCAN TO TAKE ON ${username.toUpperCase()}`;

/**
 * Where a Challenge Me QR and link land: the player's public profile, scrolled to its "Play <name>" card.
 * The profile's route is `/profile/<name>` (the client opens it as a deep link) and `#challenge` is that card.
 */
export const CHALLENGE_ANCHOR = 'challenge';
export function challengeMeUrl(origin: string, username: string): string {
  return `${origin}/profile/${encodeURIComponent(username)}#${CHALLENGE_ANCHOR}`;
}

/** The line that travels with a Challenge Me card: the result, then the invitation. */
export function challengeMeText(outcome: CardOutcome): string {
  const said = outcome.won
    ? `I won${outcome.chain > 0 ? ` +${formatScore(outcome.chain)} $CHAIN` : ''} on 21 Wild`
    : `I played ${outcome.opponent} on 21 Wild`;
  return `${said} \u2014 challenge me`;
}

/** The take link with its scheme — what the QR encodes; a complete or solo card points at the front door. */
export function takeUrl(origin: string, code: string | null | undefined): string {
  return code ? `${origin}/?code=${code}` : origin;
}

/** `host/?code=…` as printed; a complete or solo card prints the host alone. */
export function displayUrl(host: string, code: string | null | undefined): string {
  return code ? `${host}/?code=${code}` : host;
}

/** The code the card carries: only while the challenge is open. */
export function openCode(challenge: CardChallenge | undefined): string | null {
  return challenge && challenge.status === 'open' ? challenge.code : null;
}

/**
 * Hide until taken: a creator's open challenge (it carries a code) says "beat my score" and no
 * number — on the card and in the line that travels with it. A taker's seat, a settled challenge
 * and a solo round show the score as they always did.
 */
export function hidesScore(challenge: CardChallenge | undefined): boolean {
  return openCode(challenge) !== null;
}


/* --------------------------------------------------------------------------
   Metrics (px at export size)
   -------------------------------------------------------------------------- */

/** The plate's look, shared by both sizes (the renderer paints these). */
export const PLATE = {
  fill: 'rgba(10,7,16,0.94)',
  rim: 'rgba(255,255,255,0.12)',
  rimW: 2,
  shadow: 'rgba(5,3,8,0.6)',
  shadowBlur: 48,
  /** The fade above the top edge ends at the plate colour at this alpha. */
  fadeAlpha: 0.5,
} as const;

const STORY_BASE = {
  // The plate over the felt; the wordmark above it stays clear.
  plate: { x: 48, y: 1000, w: 984, h: 894, r: 40, fade: 160 },
  padX: 48,
  // Small text is budgeted for the phone: a story is viewed at ~390 css px (×0.36).
  eyebrowY: 1030,
  eyebrowSize: 32,
  scoreY: 1122,
  scoreSize: 180,
  scoreGlow: 48,
  // Score → 36 → tiles → 32 → pill → 40 → pot: one axis, everything centred. The label and the value
  // read as two lines, not a stack: the air between the label's baseline and the value's cap line is
  // about a quarter of the value's size (19 px in the 1080-wide card, 13 at the 720-wide phone view),
  // the pair centred in the tile (19 over / 20 under).
  tilesY: 1217,
  tileH: 98,
  tileGap: 24,
  tileR: 24,
  tileLabelSize: 26,
  tileLabelDy: 25,
  tileValueSize: 56,
  tileValueDy: 69,
  pillY: 1347,
  pillH: 76,
  pillPadX: 32,
  badge: 52,
  badgeGap: 16,
  nameSize: 40,
  nameGap: 20,
  rankSize: 26,
  // The pot (or the outcome): the second hero, a full-width tinted block.
  pot: { y: 1463, h: 196, r: 24 },
  potEyebrowY: 1495,
  potEyebrowSize: 38,
  potY: 1571,
  potSize: 120,
  potUnitSize: 48,
  potCoin: 72,
  potGap: 18,
  potStakeY: 1631,
  potStakeSize: 32,
  // Solo: the front door instead of the pot, the QR under it.
  ctaLabelY: 1506,
  ctaLabelSize: 32,
  hostY: 1572,
  hostSize: 48,
  soloRowY: 1662,
  // Complete: the front door in the dare row beside the QR, the row a touch lower (no link line under it).
  rowSettledY: 1696,
  rowLabelDy: 48,
  rowHostDy: 112,
  // The dare row: the code slab and the QR tile as one band.
  rowY: 1679,
  rowGap: 24,
  qrTile: 160,
  qrPad: 14,
  qrR: 20,
  slabH: 160,
  slabR: 22,
  slabSize: 96,
  slabTrack: 0.32,
  linkY: 1866,
  linkSize: 30,
};
/**
 * The story card fills the phone: the plate runs from `STORY_PLATE_TOP` to the foot and everything on it is
 * `STORY_SCALE` times the size the metrics above were drawn at, and the art is zoomed in on the wordmark and
 * held to the top — so no band of empty sky, no gap between the wordmark and the plate. Scaled from the
 * metrics, not retyped, so the composition's proportions stay exactly as reviewed.
 */
const STORY_SCALE = 1.3;
const STORY_FOOT = 1894;
const STORY_PLATE_TOP = Math.round(STORY_FOOT - STORY_BASE.plate.h * STORY_SCALE);
/** Metrics that are not sizes of anything on the plate's face: its left edge and width, the text's tracking, a horizontal gap. */
const STORY_FIXED = new Set(['x', 'w', 'padX', 'slabTrack', 'rowGap']);

function scaleStory<T extends Record<string, unknown>>(base: T, nested = false): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(base)) {
    if (typeof value === 'object' && value !== null) {
      out[key] = scaleStory(value as Record<string, unknown>, true);
    } else if (typeof value === 'number') {
      const isY = key === 'y' || /Y$/.test(key);
      if (isY) out[key] = Math.round(STORY_PLATE_TOP + (value - STORY_BASE.plate.y) * STORY_SCALE);
      else if (STORY_FIXED.has(key) && (key !== 'w' || nested)) out[key] = value;
      else out[key] = Math.round(value * STORY_SCALE);
    } else out[key] = value;
  }
  return out as T;
}

const STORY_SCALED = scaleStory(STORY_BASE);
const STORY = {
  ...STORY_SCALED,
  // The outcome block is the pot's slot grown by `outcomeExtra`, the "vs <name> · <score>" line moving down by all
  // of it: the payout's comma hung ~6.5 px (ink) over that line; now 20 (13 at the 720 view), the panel's padding
  // unchanged (21 over the eyebrow / 20 under the line's comma). The settled row drops half of it, so the gaps over
  // and under it stay even (48 / 49 before, 41 / 42 after).
  outcomeExtra: 14,
  rowSettledY: STORY_SCALED.rowSettledY + 7,
  // The plate keeps its x and width; it only grows upward.
  plate: { ...scaleStory({ plate: STORY_BASE.plate }).plate, x: STORY_BASE.plate.x, w: STORY_BASE.plate.w, y: STORY_PLATE_TOP, h: STORY_FOOT - STORY_PLATE_TOP },
  // The wordmark's box if the art does not load (it is painted into the art otherwise), up under the zoom.
  wordmark: { y: 100, w: 960, h: 574 },
};

/** The art's zoom: the wordmark's top lands ~150 px down the card and the sky above it is cropped away. */
const STORY_ART_ZOOM = 1.2;
const STORY_ART_CROP = (() => {
  const w = CARD_PX.story.w / STORY_ART_ZOOM;
  const h = CARD_PX.story.h / STORY_ART_ZOOM;
  return { x: (CARD_PX.story.w - w) / 2, y: 295, w, h };
})();


const LINK = {
  // The plate along the bottom, over the felt; the wordmark stays clear.
  plate: { x: 32, y: 338, w: 1136, h: 260, r: 32, fade: 160 },
  padX: 28,
  wordmark: { y: 36, w: 560, h: 290 },
  // Left: the score block, the stat strip and the identity pill.
  leftX: 60,
  leftW: 380,
  eyebrowY: 358,
  eyebrowSize: 20,
  scoreY: 426,
  scoreSize: 130,
  scoreGlow: 32,
  stripY: 478,
  stripH: 40,
  stripR: 12,
  stripLabelSize: 12,
  stripValueSize: 20,
  stripGap: 6,
  pillY: 522,
  pillH: 40,
  pillPadX: 14,
  badge: 30,
  badgeGap: 8,
  nameSize: 20,
  nameGap: 10,
  rankSize: 13,
  // Centre: the pot over the code (80 px from each neighbour).
  midX: 520,
  midW: 360,
  pot: { y: 352, h: 130, r: 16 },
  potEyebrowY: 374,
  potEyebrowSize: 22,
  potY: 420,
  potSize: 68,
  potUnitSize: 30,
  potCoin: 46,
  potGap: 10,
  potStakeY: 464,
  potStakeSize: 15,
  ctaLabelY: 442,
  ctaLabelSize: 16,
  hostY: 486,
  hostSize: 34,
  // Complete: the front door under the outcome, in the slab's slot.
  // The outcome block's growth, as the story's (the vs line ~21 px under the payout's comma, 12.6 at the 720 view),
  // and the door's two lines 4 lower under the taller panel.
  outcomeExtra: 7,
  rowLabelY: 522,
  rowHostY: 560,
  slabY: 494,
  slabH: 64,
  slabR: 14,
  slabSize: 42,
  slabTrack: 0.3,
  // Right: the QR, its top on the pot's line.
  qrY: 352,
  qrTile: 180,
  qrPad: 14,
  qrR: 20,
  scanGap: 16,
  scanSize: 13,
  // The link along the plate's bottom edge.
  linkY: 581,
  linkSize: 15,
} as const;

/* --------------------------------------------------------------------------
   Layout
   -------------------------------------------------------------------------- */

export function layoutCard(input: CardInput, measure: Measure = estimateWidth): CardLayout {
  return input.size === 'story' ? layoutStory(input, measure) : layoutLink(input, measure);
}

/** A run at its set size, shrunk only if it would spill past `maxW` (the score, the code, a long host). */
function fit(
  text: string,
  base: number,
  maxW: number,
  measure: Measure,
  face: Face = 'display',
  weight = 700,
  track = 0,
): number {
  const w = measure(text, face, base, weight, track);
  return w <= maxW ? base : Math.floor((base * maxW) / w);
}

/** Inner padding of the code slab: the code never touches its dashed rim. */
const SLAB_PAD_EM = 0.36;

function text(
  id: string,
  t: string,
  x: number,
  y: number,
  size: number,
  o: Partial<Omit<TextOp, 'kind' | 'id' | 'text' | 'x' | 'y' | 'size'>> = {},
): TextOp {
  return {
    kind: 'text',
    id,
    text: t,
    x,
    y,
    size,
    face: o.face ?? 'display',
    weight: o.weight ?? 700,
    tone: o.tone ?? 'text',
    align: o.align ?? 'center',
    track: o.track ?? 0,
    glow: o.glow ?? 0,
    alpha: o.alpha ?? 1,
  };
}

/** Label tracking (the shell's `--track-label`). */
export const TRACK_LABEL = 0.12;
/** Display tracking for the score (`--track-display`). */
export const TRACK_DISPLAY = 0.04;
/** Names and tile values: a touch of air, less than a label. */
export const TRACK_NUMBER = 0.02;

/** A glass panel: a rounded fill with a rim, white for the tiles and the pill, its tone for the pot and the outcome. */
function panel(
  id: string,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  tone: Tone,
  glass: { fill: number; edge: number },
): PanelOp {
  return { kind: 'panel', id, x, y, w, h, r, tone, fill: glass.fill, edge: glass.edge };
}

/**
 * The identity pill: badge · username · "DEUCE II · XP LV 2" on one line
 * inside a rounded glass pill, centred on `cx`.
 */
function pillOps(
  user: CardUser,
  cx: number,
  y: number,
  m: {
    pillH: number;
    pillPadX: number;
    badge: number;
    badgeGap: number;
    nameSize: number;
    nameGap: number;
    rankSize: number;
  },
  measure: Measure,
  /** The widest the pill may be (its column): a long name shrinks the pill's type to fit instead of spilling. */
  maxW: number = Infinity,
): CardOp[] {
  const line = rankLine(user);
  const natural =
    measure(user.username, 'display', m.nameSize, 700, TRACK_NUMBER) +
    measure(line, 'display', m.rankSize, 600, TRACK_LABEL);
  const fixed = m.badge + m.badgeGap + m.nameGap + 2 * m.pillPadX;
  // Only a name too long for the column changes anything: every other pill is exactly as it was.
  const k = fixed + natural <= maxW ? 1 : Math.max(0.5, (maxW - fixed) / natural);
  const nameSize = k < 1 ? Math.floor(m.nameSize * k) : m.nameSize;
  const rankSize = k < 1 ? Math.floor(m.rankSize * k) : m.rankSize;
  const nameW = measure(user.username, 'display', nameSize, 700, TRACK_NUMBER);
  const lineW = measure(line, 'display', rankSize, 600, TRACK_LABEL);
  const inner = m.badge + m.badgeGap + nameW + m.nameGap + lineW;
  const w = inner + 2 * m.pillPadX;
  const x0 = cx - w / 2;
  const cy = y + m.pillH / 2;
  const bx = x0 + m.pillPadX;
  const nx = bx + m.badge + m.badgeGap;
  return [
    panel('pill', x0, y, w, m.pillH, m.pillH / 2, 'text', GLASS),
    { kind: 'badge', x: bx, y: cy - m.badge / 2, size: m.badge, rank: user.rank },
    text('name', user.username, nx, cy, nameSize, { align: 'left', track: TRACK_NUMBER }),
    text('rank', line, nx + nameW + m.nameGap, cy, rankSize, {
      align: 'left',
      weight: 600,
      tone: 'rank',
      track: TRACK_LABEL,
    }),
  ];
}

/** Three equal stat tiles in one row across `totalW`: the label over the value, the game's stats-grid language. */
function tileOps(
  final: FinalScore,
  x0: number,
  y: number,
  totalW: number,
  m: {
    tileH: number;
    tileGap: number;
    tileR: number;
    tileLabelSize: number;
    tileLabelDy: number;
    tileValueSize: number;
    tileValueDy: number;
  },
): CardOp[] {
  const tiles = statTiles(final);
  const w = (totalW - m.tileGap * (tiles.length - 1)) / tiles.length;
  return tiles.flatMap((t, i) => {
    const x = x0 + i * (w + m.tileGap);
    const cx = x + w / 2;
    return [
      panel(`tile-${i}`, x, y, w, m.tileH, m.tileR, 'text', GLASS),
      text(`tile-${i}-label`, t.label, cx, y + m.tileLabelDy, m.tileLabelSize, {
        tone: 'dim',
        weight: 600,
        track: TRACK_LABEL,
      }),
      text(`tile-${i}-value`, t.value, cx, y + m.tileValueDy, m.tileValueSize, {
        tone: t.tone,
        track: TRACK_NUMBER,
      }),
    ];
  });
}

/** The link card's stat strip: one glass tile with the three label·value pairs in thirds. */
function stripOps(
  final: FinalScore,
  x0: number,
  y: number,
  totalW: number,
  m: {
    stripH: number;
    stripR: number;
    stripLabelSize: number;
    stripValueSize: number;
    stripGap: number;
  },
  measure: Measure,
): CardOp[] {
  const tiles = statTiles(final);
  const third = totalW / tiles.length;
  const cy = y + m.stripH / 2;
  return [
    panel('strip', x0, y, totalW, m.stripH, m.stripR, 'text', GLASS),
    ...tiles.flatMap((t, i) => {
      const lw = measure(t.label, 'display', m.stripLabelSize, 600, TRACK_LABEL);
      const vw = measure(t.value, 'display', m.stripValueSize, 700, TRACK_NUMBER);
      const lx = x0 + i * third + (third - (lw + m.stripGap + vw)) / 2;
      return [
        text(`strip-${i}-label`, t.label, lx, cy, m.stripLabelSize, {
          align: 'left',
          tone: 'dim',
          weight: 600,
          track: TRACK_LABEL,
        }),
        text(`strip-${i}-value`, t.value, lx + lw + m.stripGap, cy, m.stripValueSize, {
          align: 'left',
          tone: t.tone,
          track: TRACK_NUMBER,
        }),
      ];
    }),
  ];
}

type HeroMetrics = {
  pot: { y: number; h: number; r: number };
  potEyebrowY: number;
  potEyebrowSize: number;
  potY: number;
  potSize: number;
  potUnitSize: number;
  potCoin: number;
  potGap: number;
  potStakeY: number;
  potStakeSize: number;
};

/**
 * A second-hero block: a tinted panel with an eyebrow over a hero number
 * (the coin at cap height before it when it is money coming in, the unit
 * after) and a sub-line under it. The pot and the outcome are the same
 * block in different words and tones.
 */
function heroBlockOps(
  ids: { panel: string; eyebrow: string; value: string; unit: string; sub: string },
  copy: { eyebrow: string; value: string; unit: string; sub: string },
  tone: Tone,
  coin: boolean,
  x: number,
  w: number,
  m: HeroMetrics,
  measure: Measure,
): CardOp[] {
  const cx = x + w / 2;
  // A figure too wide for its block (a four-digit prize in the link card's narrow column) shrinks, number, unit,
  // coin and gaps together, to keep a margin to the block's edge; every figure that fits is untouched.
  const widthAt = (k: number): number =>
    (coin ? (m.potCoin + m.potGap) * k : 0) +
    measure(copy.value, 'display', m.potSize * k, 700, TRACK_DISPLAY) +
    m.potGap * k +
    measure(copy.unit, 'display', m.potUnitSize * k, 700, TRACK_LABEL);
  const room = w - 2 * m.pot.r;
  const k = widthAt(1) <= room ? 1 : room / widthAt(1);
  if (k < 1) m = { ...m, potSize: Math.floor(m.potSize * k), potUnitSize: Math.floor(m.potUnitSize * k), potCoin: Math.floor(m.potCoin * k), potGap: Math.floor(m.potGap * k) };
  const valueW = measure(copy.value, 'display', m.potSize, 700, TRACK_DISPLAY);
  const unitW = measure(copy.unit, 'display', m.potUnitSize, 700, TRACK_LABEL);
  const coinW = coin ? m.potCoin + m.potGap : 0;
  const total = coinW + valueW + m.potGap + unitW;
  const x0 = cx - total / 2;
  const ops: CardOp[] = [
    panel(ids.panel, x, m.pot.y, w, m.pot.h, m.pot.r, tone, POT_GLASS),
    text(ids.eyebrow, copy.eyebrow, cx, m.potEyebrowY, m.potEyebrowSize, {
      tone,
      weight: 600,
      track: TRACK_LABEL,
    }),
  ];
  // The coin and the unit centre on the number's CAP box, not its em box.
  if (coin) ops.push({ kind: 'coin', x: x0, y: m.potY - CAP_MID_EM * m.potSize - m.potCoin / 2, size: m.potCoin });
  ops.push(
    text(ids.value, copy.value, x0 + coinW, m.potY, m.potSize, {
      align: 'left',
      tone,
      track: TRACK_DISPLAY,
      glow: m.potSize * POT_GLOW_EM,
    }),
    text(ids.unit, copy.unit, x0 + coinW + valueW + m.potGap, m.potY - CAP_MID_EM * (m.potSize - m.potUnitSize), m.potUnitSize, {
      align: 'left',
      tone,
      track: TRACK_LABEL,
    }),
    text(ids.sub, copy.sub, cx, m.potStakeY, m.potStakeSize, {
      face: 'body',
      weight: 500,
      tone: 'dim',
    }),
  );
  return ops;
}

/**
 * The pot block: an amber panel with "BEAT 9,520 TO WIN" over the pot
 * ("200" at hero size, the coin at cap height before it, "$CHAIN" after)
 * and "Stake 100 to play" under it — the card's second hero.
 */
function potOps(entryFee: number, x: number, w: number, m: HeroMetrics, measure: Measure): CardOp[] {
  const c = potCopy(entryFee);
  return heroBlockOps(
    { panel: 'pot', eyebrow: 'pot-eyebrow', value: 'pot', unit: 'pot-unit', sub: 'pot-stake' },
    { eyebrow: c.eyebrow, value: c.value, unit: c.unit, sub: c.stake },
    'amber',
    true,
    x,
    w,
    m,
    measure,
  );
}

/**
 * The outcome block, in the pot's slot and weight: "YOU WON" over "+20
 * $CHAIN" with the coin (mint), or "YOU LOST" over "−10 $CHAIN" (rose, no
 * coin), and "vs mason · 12,310" under it.
 */
function outcomeOps(o: CardOutcome, x: number, w: number, m: HeroMetrics & { outcomeExtra: number }, measure: Measure): CardOp[] {
  const c = outcomeCardCopy(o);
  return heroBlockOps(
    { panel: 'outcome', eyebrow: 'outcome-eyebrow', value: 'outcome', unit: 'outcome-unit', sub: 'outcome-vs' },
    { eyebrow: c.eyebrow, value: c.value, unit: c.unit, sub: c.vs },
    c.tone,
    o.won,
    x,
    w,
    outcomeMetrics(m),
    measure,
  );
}

/** The hero metrics of the outcome block: the pot's, the panel taller and the vs line lower by `outcomeExtra`. */
function outcomeMetrics<T extends HeroMetrics & { outcomeExtra: number }>(m: T): HeroMetrics {
  return { ...m, pot: { ...m.pot, h: m.pot.h + m.outcomeExtra }, potStakeY: m.potStakeY + m.outcomeExtra };
}

/**
 * Ink metrics, measured on a real render (GPU Chrome, the display and body faces, a middle baseline; em of the
 * run's size): the payout figure's comma hangs this far under its middle line, the "vs …" line's tallest glyph
 * rises this far over its own, its comma hangs this far under it, and the eyebrow's caps rise this far over theirs.
 * Blockari 49da265's numbers: the two products share the faces.
 */
export const FIGURE_COMMA_DESCENT_EM = 0.338;
export const SUB_INK_ASCENT_EM = 0.448;
export const SUB_INK_DESCENT_EM = 0.412;
export const EYEBROW_INK_ASCENT_EM = 0.427;

/** The pot number's glow, relative to its size (the score's is 0.24). */
const POT_GLOW_EM = 0.2;
/** With a middle baseline the display face's cap box sits this far above the em centre, in em. */
export const CAP_MID_EM = 0.093;

/** The code slab, its code shrunk if six wide glyphs would touch the rim. */
function slabOp(
  code: string,
  x: number,
  y: number,
  w: number,
  m: { slabH: number; slabR: number; slabSize: number; slabTrack: number },
  measure: Measure,
): SlabOp {
  const inner = w - 2 * SLAB_PAD_EM * m.slabSize;
  return {
    kind: 'slab',
    id: 'code',
    x,
    y,
    w,
    h: m.slabH,
    r: m.slabR,
    text: code,
    size: fit(code, m.slabSize, inner, measure, 'display', 700, m.slabTrack),
    track: m.slabTrack,
  };
}

function qrOp(
  text: string,
  x: number,
  y: number,
  m: { qrTile: number; qrPad: number; qrR: number },
): QrOp {
  return { kind: 'qr', id: 'qr', x, y, size: m.qrTile, pad: m.qrPad, r: m.qrR, text };
}

function plateOp(p: { x: number; y: number; w: number; h: number; r: number; fade: number }): PlateOp {
  return { kind: 'plate', ...p };
}

/** "PLAY FREE AT" over the host, centred on `cx`, the host shrunk to `maxW`. */
function frontDoorOps(
  host: string,
  cx: number,
  labelY: number,
  labelSize: number,
  hostY: number,
  hostSize: number,
  maxW: number,
  glow: number,
  measure: Measure,
  label: string = CTA_LABEL,
): CardOp[] {
  return [
    // The label shrinks to the door's column like the line under it (a long name is a long label).
    text('cta-label', label, cx, labelY, fit(label, labelSize, maxW, measure, 'display', 600, TRACK_LABEL), {
      tone: 'amber',
      weight: 600,
      track: TRACK_LABEL,
    }),
    text('host', host, cx, hostY, fit(host, hostSize, maxW, measure, 'display', 700, TRACK_NUMBER), {
      tone: 'mint',
      track: TRACK_NUMBER,
      glow,
    }),
  ];
}

/**
 * What a settled card's door says and where its QR goes: the front door ("PLAY FREE AT", the host, the site), or
 * — shared from the settled screen — "CHALLENGE ME" over "SCAN TO TAKE ON <NAME>", the QR on the profile.
 */
function doorCopy(input: CardInput): { label: string; line: string; qr: string } {
  const cm = input.challengeMe;
  if (cm) return { label: scanToTake(cm.username), line: CHALLENGE_ME_HERO, qr: challengeMeUrl(input.origin, cm.username) };
  return { label: CTA_LABEL, line: input.host, qr: takeUrl(input.origin, null) };
}

/**
 * The eyebrow and the hero under it: "SCORE" over the number — or, on an open challenge, the number
 * is withheld and the same slot says "BEAT MY SCORE" (shrunk to fit like any hero, never a digit).
 */
function heroScoreOps(
  input: CardInput,
  cx: number,
  maxW: number,
  m: { eyebrowY: number; eyebrowSize: number; scoreY: number; scoreSize: number; scoreGlow: number },
  measure: Measure,
): CardOp[] {
  const hidden = hidesScore(input.challenge);
  const hero = hidden ? HIDDEN_SCORE : formatScore(input.final.total);
  return [
    text('eyebrow', hidden ? HIDDEN_SCORE_LABEL : SCORE_LABEL, cx, m.eyebrowY, m.eyebrowSize, {
      tone: 'dim',
      weight: 600,
      track: TRACK_LABEL,
    }),
    text('score', hero, cx, m.scoreY, fit(hero, m.scoreSize, maxW, measure, 'display', 700, TRACK_DISPLAY), {
      tone: 'mint',
      track: TRACK_DISPLAY,
      glow: m.scoreGlow,
    }),
  ];
}

function layoutStory(input: CardInput, measure: Measure): CardLayout {
  const { w, h } = CARD_PX.story;
  const M = STORY;
  const cx = w / 2;
  const x0 = M.plate.x + M.padX;
  const contentW = M.plate.w - 2 * M.padX;
  const final = input.final;
  const ops: CardOp[] = [
    { kind: 'art', src: CARD_ART.story, w, h, crop: STORY_ART_CROP, wordmark: { src: WORDMARK_SRC, cx, ...M.wordmark } },
    plateOp(M.plate),
  ];

  // "SCORE" → the hero → the stat tiles → the identity pill.
  ops.push(
    ...heroScoreOps(input, cx, contentW, M, measure),
    ...tileOps(final, x0, M.tilesY, contentW, M),
  );
  if (input.user && !input.anonymous) ops.push(...pillOps(input.user, cx, M.pillY, M, measure, contentW));

  const qrX = x0 + contentW - M.qrTile;
  const code = openCode(input.challenge);
  const c = input.challenge;
  if (c && c.status === 'complete' && c.outcome) {
    // The outcome where the pot was; the front door (or "Challenge Me") beside the QR.
    const leftW = contentW - M.qrTile - M.rowGap;
    const door = doorCopy(input);
    ops.push(
      ...outcomeOps(c.outcome, x0, contentW, M, measure),
      ...frontDoorOps(
        door.line,
        x0 + leftW / 2,
        M.rowSettledY + M.rowLabelDy,
        M.ctaLabelSize,
        M.rowSettledY + M.rowHostDy,
        M.hostSize,
        leftW,
        20,
        measure,
        door.label,
      ),
      qrOp(door.qr, qrX, M.rowSettledY, M),
    );
  } else if (c && code) {
    // The stake → code + QR.
    const slabW = contentW - M.qrTile - M.rowGap;
    if (c.entryFee !== undefined) ops.push(...potOps(c.entryFee, x0, contentW, M, measure));
    else
      ops.push(
        text('cta-label', 'TAKE THE CHALLENGE', cx, M.potY, M.ctaLabelSize, {
          tone: 'amber',
          weight: 600,
          track: TRACK_LABEL,
        }),
      );
    ops.push(
      slabOp(code, x0, M.rowY + (M.qrTile - M.slabH) / 2, slabW, M, measure),
      qrOp(takeUrl(input.origin, code), qrX, M.rowY, M),
    );
  } else {
    ops.push(
      ...frontDoorOps(input.host, cx, M.ctaLabelY, M.ctaLabelSize, M.hostY, M.hostSize, contentW, 20, measure),
      // No code to sit beside it: the QR takes the middle of the row.
      qrOp(takeUrl(input.origin, null), cx - M.qrTile / 2, M.soloRowY, M),
    );
  }

  // The take link on ONE line, shrunk until it fits the plate; never wrapped.
  // Only the dare has one: elsewhere the host is already on the card, in mint.
  if (code) {
    const link = displayUrl(input.host, code);
    ops.push(
      text('link', link, cx, M.linkY, fit(link, M.linkSize, contentW, measure, 'body', 500), {
        face: 'body',
        weight: 500,
        tone: 'dim',
      }),
    );
  }

  return { size: 'story', w, h, ops };
}

function layoutLink(input: CardInput, measure: Measure): CardLayout {
  const { w, h } = CARD_PX.link;
  const M = LINK;
  const final = input.final;
  const ops: CardOp[] = [
    { kind: 'art', src: CARD_ART.link, w, h, wordmark: { src: WORDMARK_SRC, cx: w / 2, ...M.wordmark } },
    plateOp(M.plate),
  ];

  // Left: "SCORE" → the hero → the stat strip → the identity pill.
  const leftCx = M.leftX + M.leftW / 2;
  ops.push(
    ...heroScoreOps(input, leftCx, M.leftW, M, measure),
    ...stripOps(final, M.leftX, M.stripY, M.leftW, M, measure),
  );
  if (input.user && !input.anonymous) ops.push(...pillOps(input.user, leftCx, M.pillY, M, measure, M.leftW));

  // Centre: the pot over the code; complete: the outcome over the front door; solo: the front door.
  const midCx = M.midX + M.midW / 2;
  const code = openCode(input.challenge);
  const c = input.challenge;
  const door = doorCopy(input);
  if (c && c.status === 'complete' && c.outcome) {
    ops.push(
      ...outcomeOps(c.outcome, M.midX, M.midW, M, measure),
      ...frontDoorOps(door.line, midCx, M.rowLabelY, M.ctaLabelSize, M.rowHostY, M.hostSize, M.midW, 14, measure, door.label),
    );
  } else if (c && code) {
    if (c.entryFee !== undefined) ops.push(...potOps(c.entryFee, M.midX, M.midW, M, measure));
    else
      ops.push(
        text('cta-label', 'TAKE THE CHALLENGE', midCx, M.potY, M.ctaLabelSize, {
          tone: 'amber',
          weight: 600,
          track: TRACK_LABEL,
        }),
      );
    ops.push(slabOp(code, M.midX, M.slabY, M.midW, M, measure));
  } else {
    ops.push(...frontDoorOps(input.host, midCx, M.ctaLabelY, M.ctaLabelSize, M.hostY, M.hostSize, M.midW, 14, measure));
  }

  // Right: the QR with its label.
  const qrX = M.plate.x + M.plate.w - M.padX - M.qrTile;
  ops.push(
    qrOp(c?.status === 'complete' && c.outcome ? door.qr : takeUrl(input.origin, code), qrX, M.qrY, M),
    text('scan', SCAN_LABEL, qrX + M.qrTile / 2, M.qrY + M.qrTile + M.scanGap, M.scanSize, {
      tone: 'dim',
      weight: 600,
      track: TRACK_LABEL,
    }),
  );

  // The take link along the plate's bottom edge, one line — the dare's only.
  if (code) {
    const link = displayUrl(input.host, code);
    const plateW = M.plate.w - 2 * M.padX;
    ops.push(
      text('link', link, midCx, M.linkY, fit(link, M.linkSize, plateW, measure, 'body', 500), {
        face: 'body',
        weight: 500,
        tone: 'dim',
      }),
    );
  }

  return { size: 'link', w, h, ops };
}
